import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { getPostBySlug } from '@/data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) return <Navigate to="/blog" replace />;

  const url = `https://precisionvapour.co.nz/blog/${post.slug}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    author: { '@type': 'Organization', name: post.author },
    publisher: {
      '@type': 'Organization',
      name: 'Precision Vapour Blasting',
      logo: {
        '@type': 'ImageObject',
        url: 'https://precisionvapour.co.nz/og-image.png',
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    image: 'https://precisionvapour.co.nz/og-image.png',
    keywords: post.tags.join(', '),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://precisionvapour.co.nz/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://precisionvapour.co.nz/blog' },
      { '@type': 'ListItem', position: 3, name: post.title, item: url },
    ],
  };

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>{post.title} | Precision Vapour Blasting Christchurch</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />
      <main className="flex-1 pt-32 pb-20 bg-background">
        <article className="container-wide mx-auto max-w-3xl px-4 md:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>

          <header className="mb-12">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-6">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(post.date).toLocaleDateString('en-NZ', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {post.readMinutes} min read
              </span>
              {post.tags.map((t) => (
                <span
                  key={t}
                  className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] tracking-wide uppercase"
                >
                  {t}
                </span>
              ))}
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground leading-tight">
              {post.title}
            </h1>
          </header>

          <div className="prose-invert max-w-none space-y-6">
            {post.content.map((block, i) => {
              if (block.type === 'h2') {
                return (
                  <h2
                    key={i}
                    className="font-display text-2xl md:text-3xl font-bold text-foreground mt-10 mb-2"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'ul') {
                return (
                  <ul key={i} className="list-disc list-outside pl-6 space-y-2 text-muted-foreground text-base md:text-lg leading-relaxed">
                    {block.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={i} className="text-muted-foreground text-base md:text-lg leading-relaxed">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-16 p-8 glass-card rounded-xl text-center">
            <h3 className="font-display text-2xl font-bold mb-3 text-foreground">
              Need vapour blasting in Christchurch?
            </h3>
            <p className="text-muted-foreground mb-6">
              Free quotes on automotive, marine and industrial parts. Based in Islington, serving
              all of Canterbury.
            </p>
            <Button variant="hero" size="lg" asChild>
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
